// =================================================================
// FolderTree.js — Arborescence de dossiers projet
// Session 28A → Session 29 Premium Redesign
// =================================================================

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ChevronRight, Folder, FolderOpen, Plus, MoreHorizontal, Edit3, FolderPlus, Trash2 } from 'lucide-react';
import { toast, confirm as confirmModal } from '../ui/Toast';

const FolderTree = ({ projectId, currentFolderId, onFolderSelect, refreshKey }) => {
  const [folders, setFolders] = useState([]);
  const [rootFileCount, setRootFileCount] = useState(0);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  // S-I — Section DOSSIERS rétractable (persiste dans localStorage)
  const [sectionCollapsed, setSectionCollapsed] = useState(() => {
    try { return localStorage.getItem('itriia.foldertree.collapsed') === '1'; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('itriia.foldertree.collapsed', sectionCollapsed ? '1' : '0'); }
    catch {}
  }, [sectionCollapsed]);

  // Inline creation state
  const [creatingInParent, setCreatingInParent] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const createInputRef = useRef(null);

  // Inline rename state
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const renameInputRef = useRef(null);

  // Context menu
  const [menuFolderId, setMenuFolderId] = useState(null);

  useEffect(() => {
    if (projectId) loadTree();
  }, [projectId, refreshKey]);

  useEffect(() => {
    if (creatingInParent !== undefined && createInputRef.current) {
      createInputRef.current.focus();
    }
  }, [creatingInParent]);

  useEffect(() => {
    if (renamingId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingId]);

  // Close menu on click outside
  useEffect(() => {
    const handler = () => setMenuFolderId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const token = () => localStorage.getItem('oracle_token');

  const loadTree = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/projects/${projectId}/folders`, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      if (res.data.success) {
        setFolders(res.data.tree || []);
        setRootFileCount(res.data.rootFileCount || 0);
      }
    } catch (e) {
      console.error('[FolderTree] Erreur chargement:', e);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ── CRUD actions ──────────────────────────────────

  const handleCreate = async () => {
    const name = newFolderName.trim();
    if (!name) { cancelCreate(); return; }

    try {
      await axios.post(`/api/projects/${projectId}/folders`, {
        name,
        parent_id: creatingInParent === 'root' ? null : creatingInParent
      }, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      toast.success(`Dossier "${name}" créé`);
      cancelCreate();
      await loadTree();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur création dossier');
    }
  };

  const cancelCreate = () => {
    setCreatingInParent(null);
    setNewFolderName('');
  };

  const handleRename = async (folderId) => {
    const name = renameValue.trim();
    if (!name) { setRenamingId(null); return; }

    try {
      await axios.put(`/api/projects/${projectId}/folders/${folderId}/rename`, { name }, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      toast.success('Dossier renommé');
      setRenamingId(null);
      await loadTree();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur renommage');
    }
  };

  const handleDelete = async (folderId, folderName, hasParent) => {
    const destination = hasParent ? 'le dossier parent' : 'la racine';
    const confirmed = await confirmModal(
      `Supprimer le dossier "${folderName}" ? Les fichiers qu'il contient seront déplacés dans ${destination}.`,
      { danger: true, confirmText: 'Supprimer' }
    );
    if (!confirmed) return;

    try {
      const res = await axios.delete(`/api/projects/${projectId}/folders/${folderId}`, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      const dest = res.data.moved_to_parent ? 'le dossier parent' : 'la racine';
      const msg = res.data.moved_files_count > 0
        ? `Dossier supprimé (${res.data.moved_files_count} fichiers déplacés dans ${dest})`
        : 'Dossier supprimé';
      toast.success(msg);

      if (currentFolderId === folderId) {
        onFolderSelect(null);
      }
      await loadTree();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur suppression');
    }
  };

  const startCreate = (parentId) => {
    setCreatingInParent(parentId);
    setNewFolderName('');
    setMenuFolderId(null);
    if (parentId !== 'root') {
      setExpandedIds(prev => new Set([...prev, parentId]));
    }
  };

  const startRename = (folder) => {
    setRenamingId(folder.id);
    setRenameValue(folder.name);
    setMenuFolderId(null);
  };

  // ── Render ────────────────────────────────────────

  const renderInlineInput = (onSubmit, onCancel, value, setValue, inputRef) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 0' }}>
      <Folder size={14} style={{ color: '#a78bfa', flexShrink: 0 }} />
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') onSubmit();
          if (e.key === 'Escape') onCancel();
        }}
        onBlur={onSubmit}
        maxLength={200}
        style={{
          flex: 1,
          padding: '3px 6px',
          fontSize: 11,
          border: '1px solid #a78bfa',
          borderRadius: 4,
          background: 'var(--input-bg)',
          color: 'var(--text)',
          outline: 'none',
          minWidth: 0
        }}
        placeholder="Nom du dossier..."
      />
    </div>
  );

  const renderContextMenu = (folder) => (
    <div
      style={{
        position: 'absolute',
        right: 0,
        top: '100%',
        zIndex: 50,
        background: 'var(--card-bg)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        border: '1px solid var(--card-border)',
        borderRadius: 10,
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        minWidth: 150,
        padding: '5px 0',
        fontSize: 11,
        color: 'var(--text)',
      }}
      onClick={e => e.stopPropagation()}
    >
      <button
        onClick={() => startRename(folder)}
        style={menuItemStyle}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(124,58,237,0.10)'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      >
        <Edit3 size={12} style={{ opacity: 0.7 }} /> Renommer
      </button>
      {folder.depth < 5 && (
        <button
          onClick={() => startCreate(folder.id)}
          style={menuItemStyle}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(124,58,237,0.10)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          <FolderPlus size={12} style={{ opacity: 0.7 }} /> Sous-dossier
        </button>
      )}
      <div style={{ height: 1, background: 'var(--card-border)', margin: '4px 0' }} />
      <button
        onClick={() => handleDelete(folder.id, folder.name, !!folder.parent_id)}
        style={{ ...menuItemStyle, color: '#ef4444' }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.10)'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      >
        <Trash2 size={12} style={{ opacity: 0.7 }} /> Supprimer
      </button>
    </div>
  );

  const renderFolderNode = (folder, level = 0) => {
    const isExpanded = expandedIds.has(folder.id);
    const isSelected = currentFolderId === folder.id;
    const hasChildren = folder.children && folder.children.length > 0;
    const isRenaming = renamingId === folder.id;
    const isMenuOpen = menuFolderId === folder.id;
    const totalCount = folder.total_file_count || folder.file_count || 0;
    const directCount = folder.file_count || 0;

    return (
      <div key={folder.id} style={{ position: 'relative' }}>
        {/* Indentation guides */}
        {level > 0 && Array.from({ length: level }).map((_, i) => (
          <div key={i} style={{
            position: 'absolute',
            left: 14 + i * 16,
            top: 0,
            bottom: 0,
            width: 1,
            background: 'var(--card-border)',
            opacity: 0.35,
            pointerEvents: 'none'
          }} />
        ))}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: '4px 6px',
            paddingLeft: 6 + level * 16,
            borderRadius: 5,
            cursor: 'pointer',
            fontSize: 11,
            color: isSelected ? '#a78bfa' : 'var(--text)',
            background: isSelected ? 'rgba(167,139,250,0.1)' : 'transparent',
            transition: 'all 0.15s ease',
            position: 'relative'
          }}
          onClick={() => {
            if (!isRenaming) {
              onFolderSelect(folder.id);
              if (hasChildren) toggleExpand(folder.id);
            }
          }}
          onMouseEnter={e => {
            if (!isSelected) {
              e.currentTarget.style.background = 'rgba(167,139,250,0.04)';
            }
          }}
          onMouseLeave={e => {
            if (!isSelected) e.currentTarget.style.background = 'transparent';
          }}
        >
          {/* Active accent bar */}
          {isSelected && (
            <div style={{
              position: 'absolute',
              left: 0,
              top: 3,
              bottom: 3,
              width: 3,
              borderRadius: 2,
              background: '#a78bfa'
            }} />
          )}

          {/* Chevron — single icon with rotation */}
          <span
            style={{
              width: 14,
              display: 'flex',
              justifyContent: 'center',
              flexShrink: 0,
              opacity: hasChildren ? 0.8 : 0.2,
              transition: 'transform 0.2s ease, opacity 0.15s'
            }}
            onClick={e => { e.stopPropagation(); if (hasChildren) toggleExpand(folder.id); }}
          >
            <ChevronRight size={12} style={{
              transition: 'transform 0.2s ease',
              transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'
            }} />
          </span>

          {/* Folder icon with smooth transition */}
          <span style={{
            display: 'flex',
            flexShrink: 0,
            transition: 'transform 0.2s ease',
            transform: isExpanded ? 'scale(1.05)' : 'scale(1)'
          }}>
            {isExpanded
              ? <FolderOpen size={14} style={{ color: '#a78bfa' }} />
              : <Folder size={14} style={{ color: isSelected ? '#a78bfa' : '#6b7280' }} />
            }
          </span>

          {/* Name or rename input */}
          {isRenaming ? (
            <input
              ref={renameInputRef}
              type="text"
              value={renameValue}
              onChange={e => setRenameValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') handleRename(folder.id);
                if (e.key === 'Escape') setRenamingId(null);
              }}
              onBlur={() => handleRename(folder.id)}
              onClick={e => e.stopPropagation()}
              maxLength={200}
              style={{
                flex: 1,
                padding: '2px 4px',
                fontSize: 11,
                border: '1px solid #a78bfa',
                borderRadius: 3,
                background: 'var(--input-bg)',
                color: 'var(--text)',
                outline: 'none',
                minWidth: 0
              }}
            />
          ) : (
            <span style={{
              flex: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontWeight: isSelected ? 600 : 400
            }}>
              {folder.name}
            </span>
          )}

          {/* File count badge — direct/total */}
          {totalCount > 0 && !isRenaming && (
            <span style={{
              fontSize: 9,
              padding: '1px 5px',
              borderRadius: 8,
              background: 'var(--input-bg)',
              color: 'var(--text-secondary)',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              {directCount}
              {totalCount !== directCount && (
                <span style={{ opacity: 0.5 }}>/{totalCount}</span>
              )}
            </span>
          )}

          {/* Menu button */}
          {!isRenaming && (
            <span
              onClick={e => {
                e.stopPropagation();
                setMenuFolderId(isMenuOpen ? null : folder.id);
              }}
              style={{
                width: 18,
                height: 18,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 3,
                flexShrink: 0,
                opacity: isMenuOpen ? 1 : 0.4,
                transition: 'opacity 0.15s'
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = 1}
              onMouseLeave={e => { if (!isMenuOpen) e.currentTarget.style.opacity = 0.4; }}
            >
              <MoreHorizontal size={12} />
            </span>
          )}

          {/* Context menu */}
          {isMenuOpen && renderContextMenu(folder)}
        </div>

        {/* Children — animated expand/collapse.
            S51 : overflow:visible quand expanded pour que le menu contextuel
            d'un sous-dossier (position absolute) ne soit pas clippé. */}
        <div style={{
          maxHeight: isExpanded ? `${(hasChildren ? folder.children.length : 0) * 200}px` : '0px',
          overflow: isExpanded ? 'visible' : 'hidden',
          transition: 'max-height 0.25s ease-in-out, opacity 0.2s ease',
          opacity: isExpanded ? 1 : 0
        }}>
          {hasChildren && folder.children.map(child => renderFolderNode(child, level + 1))}
        </div>

        {/* Inline create inside this folder */}
        {creatingInParent === folder.id && (
          <div style={{ paddingLeft: 6 + (level + 1) * 16, padding: '4px 6px' }}>
            {renderInlineInput(handleCreate, cancelCreate, newFolderName, setNewFolderName, createInputRef)}
          </div>
        )}
      </div>
    );
  };

  if (loading && folders.length === 0) {
    return (
      <div style={{ padding: '8px 12px', fontSize: 11, color: 'var(--text-secondary)' }}>
        Chargement dossiers...
      </div>
    );
  }

  // Compute total files across all folders + root
  const totalAllFiles = rootFileCount + folders.reduce((sum, f) => sum + (f.total_file_count || f.file_count || 0), 0);

  return (
    <div style={{
      borderBottom: '1px solid var(--oracle-border)',
      padding: '8px 0'
    }}>
      {/* Header — S-I : cliquable pour rétracter/étendre */}
      <div
        onClick={() => setSectionCollapsed(c => !c)}
        title={sectionCollapsed ? 'Déplier' : 'Rétracter'}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0 12px 6px',
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--text-secondary)',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <ChevronRight
            size={12}
            style={{
              transform: sectionCollapsed ? 'rotate(0deg)' : 'rotate(90deg)',
              transition: 'transform 0.18s ease',
            }}
          />
          Dossiers
          {sectionCollapsed && totalAllFiles > 0 && (
            <span style={{
              fontSize: 9, fontWeight: 500, color: 'var(--text-muted)',
              background: 'var(--input-bg)', padding: '1px 6px', borderRadius: 3,
              textTransform: 'none', letterSpacing: 0,
            }}>{totalAllFiles}</span>
          )}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); startCreate('root'); }}
          title="Nouveau dossier"
          style={{
            background: 'none',
            border: '1px solid var(--card-border)',
            borderRadius: 4,
            padding: '2px 4px',
            cursor: 'pointer',
            color: 'var(--text-secondary)',
            display: 'flex',
            alignItems: 'center',
            transition: 'all 0.2s'
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#a78bfa'; e.currentTarget.style.color = '#a78bfa'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--card-border)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
        >
          <Plus size={12} />
        </button>
      </div>

      {/* Bloc rétractable : root + dossiers */}
      {!sectionCollapsed && <>

      {/* "Tous les fichiers" (root) */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 12px',
          borderRadius: 5,
          margin: '0 6px',
          cursor: 'pointer',
          fontSize: 11,
          color: currentFolderId === null ? '#a78bfa' : 'var(--text)',
          background: currentFolderId === null ? 'rgba(167,139,250,0.1)' : 'transparent',
          fontWeight: currentFolderId === null ? 600 : 400,
          transition: 'all 0.15s ease',
          position: 'relative'
        }}
        onClick={() => onFolderSelect(null)}
        onMouseEnter={e => { if (currentFolderId !== null) e.currentTarget.style.background = 'rgba(167,139,250,0.04)'; }}
        onMouseLeave={e => { if (currentFolderId !== null) e.currentTarget.style.background = 'transparent'; }}
      >
        {/* Active accent bar */}
        {currentFolderId === null && (
          <div style={{
            position: 'absolute',
            left: 0,
            top: 3,
            bottom: 3,
            width: 3,
            borderRadius: 2,
            background: '#a78bfa'
          }} />
        )}
        <Folder size={14} style={{ color: currentFolderId === null ? '#a78bfa' : '#6b7280' }} />
        <span style={{ flex: 1 }}>Tous les fichiers</span>
        {totalAllFiles > 0 && (
          <span style={{
            fontSize: 9,
            padding: '1px 5px',
            borderRadius: 8,
            background: 'var(--input-bg)',
            color: 'var(--text-secondary)'
          }}>
            {totalAllFiles}
          </span>
        )}
      </div>

      {/* Inline create at root */}
      {creatingInParent === 'root' && (
        <div style={{ padding: '4px 12px', margin: '0 6px' }}>
          {renderInlineInput(handleCreate, cancelCreate, newFolderName, setNewFolderName, createInputRef)}
        </div>
      )}

      {/* Tree */}
      <div style={{ padding: '0 6px' }}>
        {folders.map(folder => renderFolderNode(folder, 0))}
      </div>

      </>}{/* fin bloc rétractable S-I */}
    </div>
  );
};

const menuItemStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  padding: '7px 12px',
  border: 'none',
  background: 'transparent',
  color: 'var(--text)',
  fontSize: 11,
  cursor: 'pointer',
  textAlign: 'left',
  transition: 'background 0.15s'
};

export default FolderTree;
